<template>
  <div>
    <!-- No data snackbar -->
    <v-snackbar v-model="noDataAlert" :timeout="3000" color="primary" location="top">
      <h3>No data available for this range</h3>
      <v-btn color="blue" variant="text" class="d-flex ml-auto" @click="noDataAlert = false"> Close </v-btn>
    </v-snackbar>
    <v-snackbar v-model="pdfTimeoutError" :timeout="3000" color="primary" location="top">
      <h3>Error downloading requested pdf. Please try again.</h3>
      <v-btn color="blue" variant="text" class="d-flex ml-auto" @click="pdfTimeoutError = false"> Close </v-btn>
    </v-snackbar>
    <!-- Chart -->
    <div class="airrWrapper">
      <div class="elevation-1 chartWrapper airrCard">
        <div class="chart-title d-block text-center">AgeWage Score Distribution</div>
        <base-chart :options="chartOptions" :scroll-options="chartSize" @click="(e) => chartClick(e)" />
      </div>
    </div>
    <!-- Members Modal -->
    <v-dialog
      v-model="membersDialog"
      @click:outside="membersDialog = false"
      persistent
      max-width="80vw"
      max-height="90%"
    >
      <v-card class="py-4">
        <v-card-title class="d-flex align-center justify-space-between">
          <div>
            <div>Member Analysis for the selected range:</div>
          </div>
          <div>
            <v-btn
              left
              color="primary"
              class="mr-4"
              :disabled="!provider || isSampleReport"
              :loading="processingCSV"
              @click="exportMemberCSV()"
            >
              <v-icon small class="mr-3">fas fas fa-file-download</v-icon>Download
            </v-btn>

            <v-btn rounded size="small" density="comfortable" variant="flat" icon @click="membersDialog = false">
              <v-icon small>fa fa-times</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <AppTable
            :table-data="membersTableData"
            :disableSort="true"
            :disablePagination="true"
            :default-sort-column="[{ key: 'dataId', order: 'asc' }]"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn color="grey-darken-1" variant="text" @click="membersDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <AppLoader v-if="fetchingMembersAnalysis" />
  </div>
</template>

<script lang="ts">
import { Vue, Prop } from 'vue-property-decorator';
import { Options } from 'vue-class-component';
import BaseChart from '@/components/charts/BaseChart.vue';
import _ from 'lodash';
import Utils from '../../utils/Utils';
import * as AT from '@/modules/members/store/actionTypes';
import * as PTAT from '@/modules/price-track/store/actionTypes';
import { GET_EXPORT_JOB_BY_ID } from '@/modules/reports/store/actionTypes';
import { GET_CSV_URL } from '@/modules/members/store/actionTypes';
import { Table, TableColumn, TableRow, TableColumnType } from '@/types';
import AppTable from '@/components/AppTable.vue';
import AppLoader from '@/components/AppLoder.vue';
import { PriceTrackObject } from '@/models/PriceTrack';

@Options({
  components: {
    BaseChart,
    AppTable,
    AppLoader,
  },
})
export default class ScoreDistChart extends Vue {
  @Prop({
    default: () => ({}),
  })
  chartData!: number[][];

  @Prop({
    default: () => ({ primary: '#fe7598', secondary: '#fea1be' }),
  })
  barColor!: any;

  @Prop({
    default: () => ({}),
  })
  reportfilterRanges?: any;

  @Prop({
    default: () => null,
  })
  provider!: number;

  @Prop({
    default: () => null,
  })
  priceTrack!: PriceTrackObject;

  @Prop() isSampleReport!: boolean;

  public chartSize: any = {
    chartWidth: 50,
    chartHeight: 30,
    unit: 'rem',
  };

  public membersDialog = false;

  public fetchingMembersAnalysis = false;

  public noDataAlert = false;

  public processingCSV = false;

  public selectedRange = [];

  public currentFilter: any = {};

  public pdfTimeoutError = false;

  public pagination = {
    page: 1,
    limit: 15,
  };

  private async fetchMembersAnalysis(data: any = {}) {
    this.fetchingMembersAnalysis = true;
    let analysis = null;
    if (this.isSampleReport) {
      analysis = await this.$store.dispatch(AT.FETCH_SAMPLE_MEMBERS_ANALYSIS, data);
    } else if (this.provider) {
      analysis = await this.$store.dispatch(AT.FETCH_MEMBERS_ANALYSIS, data);
    } else if (this.priceTrack) {
      analysis = await this.$store.dispatch(PTAT.FETCH_MEMBERS_ANALYSIS_PRICE_TRACK, data);
    }
    this.fetchingMembersAnalysis = false;
    return analysis;
  }

  public async chartClick(e: any) {
    if (e.dataIndex !== undefined) {
      this.noDataAlert = false;
      const index = await e.dataIndex;
      const range = this.processedData[index][0].split(',').map((it) => +it);
      const convertedFilters = Utils.convertMinMaxToArrayFilters(this.reportfilterRanges);
      if (convertedFilters.scoreRange) {
        if (convertedFilters.scoreRange[0] > range[0]) {
          range[0] = convertedFilters.scoreRange[0];
        }

        if (convertedFilters.scoreRange[1] < range[1]) {
          range[1] = convertedFilters.scoreRange[1];
        }
      }
      const appliedFilters: any = {
        ...convertedFilters,
        scoreRange: range,
      };
      if (this.provider) {
        appliedFilters.providers = [this.provider];
      } else if (this.priceTrack) {
        appliedFilters.dataBatchIds = [this.priceTrack.dataBatchId];
      }
      this.currentFilter = {
        ...this.pagination,
        ...appliedFilters,
      };
      const analysis = await this.fetchMembersAnalysis(this.currentFilter);
      analysis.length ? (this.membersDialog = true) : (this.noDataAlert = true);
    }
  }

  public get memberAnalysis(): any[] | null {
    return this.$store.getters.memberAnalysis && this.$store.getters.memberAnalysis.data;
  }

  public get membersTableData(): Table {
    const data = this.memberAnalysis ? this.memberAnalysis : [];

    const rows = data.map((it) => {
      return {
        dataId: it.dataId,
        firstContributionDate: it.firstContributionDate,
        // fundName: it.fund.name,
        score: it.scores.score || it.scores.score === 0 ? parseInt(it.scores.score) : '-',
        totalContributionAmount: it.totalContributionAmount,
        navAmount: it.analysis.navAmount,
        navDate: it.analysis.navDate,
        irr: it.analysis.irr,
        benchmarkIrr: it.analysis.benchmarkIrr,
        benchmarkNavAmount: it.analysis.benchmarkNavAmount,
      };
    });
    return {
      columns: [
        {
          value: 'dataId',
          title: 'Saver ID',
          type: TableColumnType.NUMBER,
          align: 'center',
        },
        // {
        //   value: 'fundName',
        //   text: 'Fund Name',
        //   type: TableColumnType.STRING
        // },
        {
          value: 'firstContributionDate',
          title: 'First Contribution Date',
          type: TableColumnType.DATE,
          formatter: (v: any) => Utils.formatDate(new Date(v), 'MMM dd, yyyy'),
          align: 'center',
        },
        {
          value: 'navDate',
          title: 'NAV Date',
          type: TableColumnType.DATE,
          formatter: (v: any) => Utils.formatDate(new Date(v), 'MMM dd, yyyy'),
          align: 'center',
        },
        {
          value: 'score',
          title: 'AW Score',
          type: TableColumnType.NUMBER,
          align: 'center',
        },
        {
          value: 'irr',
          title: 'IRR (%)',
          type: TableColumnType.NUMBER,
          formatter: (v: any) => (v * 100).toFixed(2),
          align: 'center',
        },
        {
          value: 'benchmarkIrr',
          title: 'Benchmark IRR (%)',
          type: TableColumnType.NUMBER,
          formatter: (v: any) => (v * 100).toFixed(2),
          align: 'center',
        },
        {
          value: 'totalContributionAmount',
          title: 'Total Contribution Amount',
          type: TableColumnType.NUMBER,
          formatter: (n) => Utils.formatNumber(n, '$0.00a'),
          align: 'center',
        },
        {
          value: 'navAmount',
          title: 'NAV Amount',
          type: TableColumnType.NUMBER,
          formatter: (n) => Utils.formatNumber(n, '$0.00a'),
          align: 'center',
        },
        {
          value: 'benchmarkNavAmount',
          title: 'Benchmark Nav Amount',
          type: TableColumnType.NUMBER,
          formatter: (n) => Utils.formatNumber(n, '$0.00a'),
          align: 'center',
        },
      ],
      rows,
    };
  }

  public async exportMemberCSV() {
    //PDF Export Polling with 2sec interval and 16sec Timout
    let job = { id: -1 };

    this.processingCSV = true;
    job = await this.$store.dispatch(AT.EXPORT_MEMBERS_ANALYSIS, {
      page: 1,
      limit: -1,
      providers: this.currentFilter.providers,
      scoreRange: this.currentFilter.scoreRange,
      irrRange: this.currentFilter.irrRange,
      birrRange: this.currentFilter.birrRange,
      potRange: this.currentFilter.potRange,
    });

    let pdfFetchInterval = setInterval(async () => {
      let response = await this.$store.dispatch(GET_EXPORT_JOB_BY_ID, job.id);
      if (response[0].status === 'completed') {
        const url = await this.$store.dispatch(GET_CSV_URL, job.id);
        await window.open(url);
        this.processingCSV = false;
        clearInterval(pdfFetchInterval);
      }
      return response;
    }, Utils.pdfInterval);
    setTimeout(() => {
      clearInterval(pdfFetchInterval);
      if (this.processingCSV) {
        this.pdfTimeoutError = true;
      }
      this.processingCSV = false;
    }, Utils.pdfTimeout);
  }

  public get processedData() {
    const uniqueScores = _.range(0, 101, 10);
    const buckets = uniqueScores.reduce((acc: number[], n: number, i: number) => {
      if (i !== 0 && i !== uniqueScores.length - 1) {
        acc.push(n);
        acc.push(n);
      } else {
        acc.push(n);
      }
      return acc;
    }, []);
    const scoreRanges = _.chunk(buckets, 2);
    const data: [string, number][] = [];
    scoreRanges.map((range, i) => {
      let items = [];
      if (i === 0) {
        items = this.chartData?.filter((irr) => irr[0] >= range[0] && irr[0] <= range[1]);
      } else {
        items = this.chartData?.filter((irr) => irr[0] > range[0] && irr[0] <= range[1]);
      }
      const key = range[0] + ',' + range[1];
      const count = items?.reduce((a, b) => a + b[1], 0);
      data.push([key, count]);
    });
    return data;
  }

  public get xAxisData() {
    const keys = this.processedData.map((r) => r[0]);
    return keys;
  }

  public get seriesData() {
    const values = this.processedData.map((item) => item[1]);
    return values;
  }

  public get chartOptions() {
    return {
      color: {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
          {
            offset: 1,
            color: '#fe7598', // color at 0% position
          },
          {
            offset: 0,
            color: '#fea1be', // color at 100% position
          },
        ],
      },
      tooltip: {
        show: true,
        formatter: (v: any) => `${v.value}`,
      },
      xAxis: [
        {
          name: 'AgeWage Score',
          nameLocation: 'middle',
          nameTextStyle: { color: '#6f6c76', padding: [30, 0, 0, 0], fontFamily: 'Open Sans', fontWeight: 'bolder' },
          type: 'category',
          axisTick: { show: false, alignWithLabel: true },
          axisLine: { show: true, lineStyle: { color: '#ccc' } },
          data: this.xAxisData,
          axisLabel: {
            rotate: '50',
            fontWeight: '600',
            fontFamily: 'Open Sans',
            color: '#6f6c76',
            formatter: (value: any) => {
              const s = '' + value;
              return s.split(',').join(' - ');
            },
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: 'No. of Pots',
          nameLocation: 'middle',
          nameRotate: 90,
          color: '#6f6c76',
          nameTextStyle: {
            color: '#6f6c76',
            padding: [0, 0, 40, 0],
            fontFamily: 'Open Sans',
            fontWeight: 'bolder',
          },
          axisLine: { show: true, lineStyle: { color: '#ccc' } },
          splitLine: {
            show: true,
            lineStyle: {
              color: '#eee',
            },
          },
          axisLabel: {
            fontWeight: '600',
            fontFamily: 'Open Sans',
            color: '#6f6c76',
            interval: false,
          },
        },
      ],
      series: [
        {
          type: 'bar',
          data: this.seriesData,
          label: {
            show: true,
            position: 'top',
            color: '#fe7598',
            rich: { name: { fontFamily: 'Open Sans', color: '#6f6c76' } },
            textBorderColor: '#fff',
          },
        },
      ],
      barWidth: 40,
      barGap: '30%',
    };
  }
}
</script>
<style lang="scss">
.airrWrapper {
  padding: 1rem 0rem !important;
  .airrCard {
    padding-bottom: 3rem !important;
  }
}
</style>
